import type { StoreApi } from 'zustand';
import { superAdminApi } from '../api/superAdminApi';
import { mergeTicketPage, reconcileTicketStatusCounts } from '../lib/adminRequests';
import type {
  AdminTicketDto,
  AdminTicketListQuery,
  AdminTicketListResponse,
  AdminTicketMessageRequest,
  AdminTicketPatchRequest,
} from '../types';
import type { SuperAdminActions, SuperAdminState } from './useSuperAdminStore';

type Store = SuperAdminState & SuperAdminActions;
type SetState = StoreApi<Store>['setState'];
type GetState = StoreApi<Store>['getState'];

const toAdminError = (error: unknown) => {
  const maybeResponse = error as { response?: { data?: { error?: { code?: string; message?: string } } } };
  const responseError = maybeResponse.response?.data?.error;
  return {
    code: responseError?.code ?? 'request_failed',
    message: responseError?.message ?? (error instanceof Error ? error.message : 'Request failed'),
  };
};

const patchListTicket = (
  tickets: SuperAdminState['requestTickets'],
  canonical: AdminTicketDto
) => tickets
  .map((item) => (item.id === canonical.id ? { ...canonical, messages: undefined } : item))
  .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt) || right.id - left.id);

const isCancelled = (error: unknown) => {
  const candidate = error as { code?: string; name?: string };
  return candidate.code === 'ERR_CANCELED' || candidate.name === 'AbortError' || candidate.name === 'CanceledError';
};

const conflictTicket = (error: unknown) => {
  const candidate = error as {
    response?: { status?: number; data?: { current?: AdminTicketDto } };
  };
  return candidate.response?.status === 409 ? candidate.response.data?.current ?? null : null;
};

const isConflict = (error: unknown) =>
  (error as { response?: { status?: number } }).response?.status === 409;

const rollbackPatchedFields = (
  current: AdminTicketDto,
  previous: AdminTicketDto,
  optimistic: Pick<AdminTicketDto, 'status' | 'priority' | 'assigneeId' | 'assigneeName'>,
  patch: AdminTicketPatchRequest
) => {
  const rolledBack = { ...current };
  if (patch.status !== undefined && current.status === optimistic.status) {
    rolledBack.status = previous.status;
  }
  if (patch.priority !== undefined && current.priority === optimistic.priority) {
    rolledBack.priority = previous.priority;
  }
  if (patch.assigneeId !== undefined && current.assigneeId === optimistic.assigneeId) {
    rolledBack.assigneeId = previous.assigneeId;
    rolledBack.assigneeName = previous.assigneeName;
  }
  return rolledBack;
};

type ApplyCanonicalTicket = (canonical: AdminTicketDto) => void;

const createCanonicalApplier = (set: SetState): ApplyCanonicalTicket => (canonical) => {
  set((state) => {
    const previous = state.requestTickets.find((item) => item.id === canonical.id);
    return {
      requestDetail: state.requestDetail?.id === canonical.id ? canonical : state.requestDetail,
      requestTickets: patchListTicket(state.requestTickets, canonical),
      requestStatusCounts: previous
        ? reconcileTicketStatusCounts(state.requestStatusCounts, previous.status, canonical.status)
        : state.requestStatusCounts,
    };
  });
};

const recoverConflict = async (
  applyCanonicalTicket: ApplyCanonicalTicket,
  id: number,
  error: unknown
) => {
  if (!isConflict(error)) {
    return false;
  }
  try {
    const canonical = conflictTicket(error) ?? await superAdminApi.getTicket(id);
    applyCanonicalTicket(canonical);
    return true;
  } catch {
    return false;
  }
};

const createTicketListActions = (set: SetState) => {
  let listGeneration = 0;
  let listController: AbortController | null = null;
  const fetchTickets = async (
    query: AdminTicketListQuery,
    mode: 'replace' | 'append' = 'replace'
  ): Promise<AdminTicketListResponse> => {
    const generation = ++listGeneration;
    listController?.abort();
    const controller = new AbortController();
    listController = controller;
    set(mode === 'append'
      ? { requestLoadingMore: true, requestError: null }
      : { requestLoading: true, requestLoadingMore: false, requestError: null });
    try {
      const response = await superAdminApi.listTickets(query, controller.signal);
      if (generation !== listGeneration) {
        return response;
      }
      set((state) => ({
        requestTickets: mode === 'append'
          ? mergeTicketPage(state.requestTickets, response.items)
          : response.items,
        requestNextCursor: response.nextCursor ?? null,
        requestStatusCounts: response.statusCounts,
        requestCategoryCounts: response.categoryCounts,
        requestLoading: false,
        requestLoadingMore: false,
        requestError: null,
      }));
      return response;
    } catch (error) {
      const requestError = toAdminError(error);
      if (generation === listGeneration) {
        set({
          requestLoading: false,
          requestLoadingMore: false,
          requestError: isCancelled(error) ? null : requestError,
        });
      }
      throw error;
    } finally {
      if (listController === controller) {
        listController = null;
      }
    }
  };

  return { fetchTickets };
};

const createTicketDetailActions = (set: SetState) => {
  let detailGeneration = 0;
  const loadTicketDetail = async (id: number) => {
    const generation = ++detailGeneration;
    set({ requestDetailLoading: true, requestError: null });
    try {
      const detail = await superAdminApi.getTicket(id);
      if (generation !== detailGeneration) {
        return detail;
      }
      set((state) => ({
        requestDetail: detail,
        requestDetailLoading: false,
        requestTickets: patchListTicket(state.requestTickets, detail),
      }));
      return detail;
    } catch (error) {
      const requestError = toAdminError(error);
      if (generation === detailGeneration) {
        set({ requestDetail: null, requestDetailLoading: false, requestError });
      }
      throw error;
    }
  };

  const clearTicketDetail = () => {
    detailGeneration += 1;
    set({ requestDetail: null, requestDetailLoading: false, requestError: null });
  };

  return { clearTicketDetail, loadTicketDetail };
};

const createTicketMutationActions = (
  set: SetState,
  get: GetState,
  applyCanonicalTicket: ApplyCanonicalTicket
) => {
  const patchTicket = async (
    id: number,
    patch: AdminTicketPatchRequest,
    optimistic?: Pick<AdminTicketDto, 'status' | 'priority' | 'assigneeId' | 'assigneeName'>
  ) => {
    const previous = get().requestDetail;
    if (previous?.id === id && optimistic) {
      const optimisticTicket = { ...previous, ...optimistic };
      applyCanonicalTicket(optimisticTicket);
    }

    try {
      const canonical = await superAdminApi.patchTicket(id, patch);
      set({ requestError: null });
      applyCanonicalTicket(canonical);
      return canonical;
    } catch (error) {
      const requestError = toAdminError(error);
      const recovered = await recoverConflict(applyCanonicalTicket, id, error);
      if (!recovered && previous?.id === id && optimistic) {
        const current = get().requestDetail;
        if (current?.id === id) {
          applyCanonicalTicket(rollbackPatchedFields(current, previous, optimistic, patch));
        }
      }
      set({ requestError });
      throw error;
    }
  };

  const sendTicketMessage = async (id: number, request: AdminTicketMessageRequest) => {
    if (get().requestSubmitting) {
      throw new Error('A ticket message is already being submitted.');
    }
    set({ requestSubmitting: true, requestError: null });
    try {
      const canonical = await superAdminApi.addTicketMessage(id, request);
      set({ requestSubmitting: false, requestError: null });
      applyCanonicalTicket(canonical);
      return canonical;
    } catch (error) {
      await recoverConflict(applyCanonicalTicket, id, error);
      set({ requestSubmitting: false, requestError: toAdminError(error) });
      throw error;
    }
  };

  return {
    patchTicket,
    sendTicketMessage,
    resolveTicket: (id: number, expectedUpdatedAt: string) =>
      patchTicket(id, { status: 'resolved', expectedUpdatedAt }, undefined),
    reopenTicket: (id: number, expectedUpdatedAt: string) =>
      patchTicket(id, { status: 'open', expectedUpdatedAt }, undefined),
  };
};

export const createRequestStoreActions = (set: SetState, get: GetState) => {
  const applyCanonicalTicket = createCanonicalApplier(set);
  return {
    ...createTicketListActions(set),
    ...createTicketDetailActions(set),
    ...createTicketMutationActions(set, get, applyCanonicalTicket),
  };
};
